import { Button, Modal, Upload } from "antd";
import { useCallback, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useImport } from "~components/ImportButton/hooks/useImport";
import "./styles.css";
import { ActionButton } from "~components/ActionButton/ActionButton.tsx";
import { useUserUpdates } from "~context/UserUpdatesContext/UserUpdatesContext.tsx";
import { useNotification } from "~hooks/useNotification";

type Props = {
  title: string;
  description: string;
  startImporting: (fileName: string) => Promise<unknown>;
  buttonType?: "button" | "icon";
};

export const ImportModal = ({ startImporting, title, description, buttonType = "icon" }: Props) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const notification = useNotification();
  const { refetch: refetchUserUpdates } = useUserUpdates();

  const { mutateAsync: handleImport, isPending: isImportPending } = useImport();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const onFileSelected = (file: File) => {
    setSelectedFile(file);
    return false;
  };

  const { formatMessage } = useIntl();

  const handleClose = useCallback(() => {
    setSelectedFile(null);
    setOpen(false);
  }, []);

  const handleOk = useCallback(async () => {
    if (!selectedFile) {
      notification.error({
        message: formatMessage({ id: "import.file.not.selected" }),
        placement: "bottomRight",
      });
      return;
    }
    setLoading(true);
    try {
      await handleImport({ file: selectedFile, startImporting });
    } catch (error) {
      // Every failing step has already notified the user, so the modal only stays open for a retry
      console.error("Import failed", error);
      return;
    } finally {
      setLoading(false);
    }
    setTimeout(() => {
      refetchUserUpdates();
    }, 3000);
    handleClose();
  }, [selectedFile, startImporting, notification.error, refetchUserUpdates, handleImport, handleClose, formatMessage]);

  return (
    <>
      <Modal
        open={open}
        onCancel={handleClose}
        onOk={handleOk}
        okText={formatMessage({ id: "import" })}
        okButtonProps={{ disabled: !selectedFile, loading: loading || isImportPending }}
      >
        <h5>{title}</h5>
        <p className="ant-upload-text">{description}</p>
        <Upload.Dragger
          disabled={loading || isImportPending}
          className="custom-dragger"
          accept="text/*"
          beforeUpload={onFileSelected}
          showUploadList={false}
        >
          <div style={{ padding: 40 }}>
            <p>
              <FormattedMessage id="import.drag.zone.body.text" />
            </p>
            <p>
              <FormattedMessage id="import.drag.zone.body.text.or" />
            </p>
            <Button disabled={loading || isImportPending} type="primary">
              <FormattedMessage id="import.drag.zone.body.button.text" />
            </Button>
          </div>
          {selectedFile ? (
            <div
              onClick={(e) => {
                e.stopPropagation();
              }}
              className="file-item"
            >
              <i className="fa-solid fa-file"></i>
              <div>{selectedFile?.name}</div>
              <i
                className="fa-solid fa-trash"
                style={{ cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedFile(null);
                }}
              ></i>
            </div>
          ) : (
            <div className="file-item-empty" />
          )}
        </Upload.Dragger>
      </Modal>
      <ActionButton
        buttonType={buttonType}
        textIntl={"import"}
        onClick={() => {
          setOpen(true);
        }}
        icon={<i className="fa-solid fa-file-import"></i>}
      />
    </>
  );
};
